/**
 * Overview tab — first thing the office user sees on a project.
 *
 * Shows the project header (id, address, inspector, status), totals from
 * the latest manifest estimate, a phase-distribution bar (how captures are
 * spread across arrival/plan/meter/equipment/before/scope), and a
 * completion checklist (manifest, inspections, scope, equipment, S500).
 *
 * Pure rendering — receives already-fetched IC + manifest data; the
 * detail-page shell owns fetching and refresh.
 */
import { api } from "../../api/client.ts";
import type {
  IcProject,
  Manifest,
  ManifestEstimate,
  ManifestInspection,
  ManifestS500Block,
} from "../../types.ts";
import { PHASE_COLORS } from "../../constants.ts";

const PHASE_ORDER = [
  "arrival",
  "plan",
  "meter",
  "equipment",
  "before",
  "scope",
] as const;

export interface OverviewTabOptions {
  container: HTMLElement;
  project: IcProject;
  manifest: Manifest | null;     // null when no manifest exists yet
  manifestVersion: number;
  manifestUpdatedAt: string | null;
}

export class OverviewTab {
  constructor(private opts: OverviewTabOptions) {
    this.render();
  }

  private render(): void {
    const root = this.opts.container;
    root.innerHTML = "";
    root.style.cssText = "padding:16px;display:flex;flex-direction:column;gap:16px;";

    root.appendChild(this.headerBlock());
    root.appendChild(this.insuranceBlock());
    root.appendChild(this.totalsBlock());
    root.appendChild(this.phaseTimelineBlock());
    root.appendChild(this.checklistBlock());
  }

  private headerBlock(): HTMLElement {
    const { project } = this.opts;
    const a = project.assignment;

    const wrap = section("Header");
    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:auto 1fr;gap:6px 12px;";

    addRow(grid, "Project", a.client_project_id || project.project_id.slice(0, 8));
    const addr = [a.address.line1, a.address.city, a.address.state, a.address.zip]
      .filter(Boolean)
      .join(", ");
    if (addr) addRow(grid, "Address", addr);
    addRow(grid, "Inspector", a.inspector || "—");
    addRow(grid, "Loss type", a.loss_type || "—");
    addRow(grid, "Status", a.status);
    if (a.contact_name || a.contact_phone) {
      addRow(
        grid,
        "Contact",
        [a.contact_name, a.contact_phone].filter(Boolean).join(" · "),
      );
    }
    addRow(grid, "Started", project.started_at ? formatDate(project.started_at) : "—");
    addRow(grid, "Updated", formatDate(project.updated_at));
    if (this.opts.manifestVersion > 0) {
      addRow(
        grid,
        "Manifest",
        `v${this.opts.manifestVersion}` +
          (this.opts.manifestUpdatedAt
            ? ` · ${formatRelative(this.opts.manifestUpdatedAt)}`
            : ""),
      );
    }

    wrap.appendChild(grid);
    return wrap;
  }

  private insuranceBlock(): HTMLElement {
    const { project } = this.opts;
    const a = project.assignment;
    const wrap = section("Insurance / Adjuster");

    const inputCss =
      "width:100%;box-sizing:border-box;background:var(--surface2);border:1px solid var(--border);border-radius:4px;padding:5px 8px;font-size:12px;color:var(--text);outline:none;";

    const fields: Array<{
      key: "policy_number" | "insurance_carrier" | "deductible" | "rcv" | "acv";
      label: string;
      type: "text" | "number";
    }> = [
      { key: "policy_number", label: "Policy Number", type: "text" },
      { key: "insurance_carrier", label: "Insurance Carrier", type: "text" },
      { key: "deductible", label: "Deductible ($)", type: "number" },
      { key: "rcv", label: "RCV ($)", type: "number" },
      { key: "acv", label: "ACV ($)", type: "number" },
    ];

    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:auto 1fr;gap:6px 12px;align-items:center;";

    const inputs: Partial<Record<string, HTMLInputElement>> = {};
    for (const f of fields) {
      const lbl = document.createElement("div");
      lbl.textContent = f.label;
      lbl.style.cssText = "color:var(--text-muted);font-size:12px;white-space:nowrap;";
      const inp = document.createElement("input");
      inp.type = f.type;
      inp.style.cssText = inputCss;
      const raw = a[f.key];
      inp.value = raw != null ? String(raw) : "";
      inp.placeholder = "—";
      if (f.type === "number") {
        inp.step = "0.01";
        inp.min = "0";
      }
      inputs[f.key] = inp;
      grid.appendChild(lbl);
      grid.appendChild(inp);
    }
    wrap.appendChild(grid);

    const footer = document.createElement("div");
    footer.style.cssText = "display:flex;align-items:center;gap:8px;margin-top:4px;";

    const saveBtn = document.createElement("button");
    saveBtn.textContent = "Save";
    saveBtn.className = "btn primary";
    saveBtn.style.cssText = "font-size:12px;padding:4px 14px;";
    footer.appendChild(saveBtn);

    const status = document.createElement("span");
    status.style.cssText = "font-size:12px;color:var(--text-muted);";
    footer.appendChild(status);
    wrap.appendChild(footer);

    saveBtn.addEventListener("click", async () => {
      saveBtn.disabled = true;
      for (const inp of Object.values(inputs)) if (inp) inp.disabled = true;
      status.textContent = "Saving…";
      status.style.color = "var(--text-muted)";

      const payload: Record<string, string | number | null> = {};
      for (const f of fields) {
        const inp = inputs[f.key];
        if (!inp) continue;
        const val = inp.value.trim();
        if (f.type === "number") {
          payload[f.key] = val === "" ? null : parseFloat(val);
        } else {
          payload[f.key] = val === "" ? null : val;
        }
      }

      try {
        await api.ic.patchInsurance(project.project_id, {
          ...payload,
          expected_state_version: project.state_version,
          client_op_id: crypto.randomUUID(),
        } as Parameters<typeof api.ic.patchInsurance>[1]);
        status.textContent = "Saved";
        status.style.color = "#4aff6a";
        setTimeout(() => { status.textContent = ""; }, 3000);
      } catch (err) {
        status.textContent = (err as Error).message || "Save failed";
        status.style.color = "#ff4a4a";
      } finally {
        saveBtn.disabled = false;
        for (const inp of Object.values(inputs)) if (inp) inp.disabled = false;
      }
    });

    return wrap;
  }

  private totalsBlock(): HTMLElement {
    const wrap = section("Estimate totals");
    const e: ManifestEstimate | null = this.opts.manifest?.estimate ?? null;

    if (!e) {
      const empty = document.createElement("div");
      empty.style.cssText = "color:var(--text-muted);font-size:13px";
      empty.textContent =
        this.opts.manifestVersion > 0
          ? "Manifest exists but no estimate computed yet."
          : "No manifest snapshot — totals will appear when mobile pushes one.";
      wrap.appendChild(empty);
      return wrap;
    }

    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:auto 1fr;gap:6px 12px;";
    addRow(grid, "Subtotal", formatMoney(e.subtotal));
    if (e.markup_percent) {
      addRow(
        grid,
        `Markup (${e.markup_percent}%)`,
        `+ ${formatMoney(e.markup_amount)}`,
      );
    }
    if (e.tax_percent) {
      addRow(grid, `Tax (${e.tax_percent}%)`, `+ ${formatMoney(e.tax_amount)}`);
    }
    const total = document.createElement("div");
    total.style.cssText =
      "grid-column:1 / -1;border-top:1px solid var(--border);padding-top:8px;margin-top:4px;display:flex;justify-content:space-between;font-weight:700;font-size:15px;color:var(--text);";
    total.innerHTML =
      `<span>Grand total</span><span>${formatMoney(e.grand_total)}</span>`;
    grid.appendChild(total);
    wrap.appendChild(grid);

    return wrap;
  }

  private phaseTimelineBlock(): HTMLElement {
    const wrap = section("Phase distribution");
    const counts = this.phaseCounts();
    const total = Object.values(counts).reduce((a, b) => a + b, 0);

    if (total === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = "color:var(--text-muted);font-size:13px;";
      empty.textContent = "No captures yet.";
      wrap.appendChild(empty);
      return wrap;
    }

    // Stacked bar
    const bar = document.createElement("div");
    bar.style.cssText =
      "display:flex;height:8px;border-radius:4px;overflow:hidden;background:var(--border);";
    for (const phase of PHASE_ORDER) {
      const n = counts[phase] || 0;
      if (n === 0) continue;
      const seg = document.createElement("div");
      seg.title = `${phase}: ${n}`;
      seg.style.cssText = `flex:${n};background:${PHASE_COLORS[phase]};`;
      bar.appendChild(seg);
    }
    if (counts.other) {
      const seg = document.createElement("div");
      seg.title = `other: ${counts.other}`;
      seg.style.cssText = `flex:${counts.other};background:${PHASE_COLORS.other};`;
      bar.appendChild(seg);
    }
    wrap.appendChild(bar);

    // Legend
    const legend = document.createElement("div");
    legend.style.cssText =
      "display:flex;flex-wrap:wrap;gap:10px;font-size:11px;color:var(--text-muted);margin-top:8px;";
    for (const phase of PHASE_ORDER) {
      const n = counts[phase] || 0;
      legend.appendChild(legendDot(phase, n, PHASE_COLORS[phase]));
    }
    if (counts.other) {
      legend.appendChild(legendDot("other", counts.other, PHASE_COLORS.other));
    }
    wrap.appendChild(legend);

    const totalLine = document.createElement("div");
    totalLine.style.cssText = "font-size:11px;color:var(--text-muted);margin-top:6px;";
    totalLine.textContent = `${total} captures across ${this.opts.manifest?.inspections.length || 0} inspection(s)`;
    wrap.appendChild(totalLine);

    return wrap;
  }

  private checklistBlock(): HTMLElement {
    const wrap = section("Completion checklist");
    const m = this.opts.manifest;
    const s500: ManifestS500Block | null = m?.s500 ?? null;

    const items: Array<{ label: string; ok: boolean; detail?: string }> = [];
    items.push({
      label: "Manifest snapshot",
      ok: this.opts.manifestVersion > 0,
      detail: this.opts.manifestVersion > 0 ? `v${this.opts.manifestVersion}` : undefined,
    });
    items.push({
      label: "Rooms documented",
      ok: (m?.rooms.length ?? 0) > 0,
      detail: m ? `${m.rooms.length} room(s)` : undefined,
    });
    items.push({
      label: "Inspections recorded",
      ok: (m?.inspections.length ?? 0) > 0,
      detail: m ? `${m.inspections.length} inspection(s)` : undefined,
    });
    items.push({
      label: "Captures uploaded",
      ok: this.totalCaptures(m?.inspections ?? []) > 0,
      detail: m ? `${this.totalCaptures(m.inspections)} capture(s)` : undefined,
    });
    items.push({
      label: "Scope items",
      ok: (m?.scope.length ?? 0) > 0,
      detail: m ? `${m.scope.length} item(s)` : undefined,
    });
    items.push({
      label: "Equipment plan (S500)",
      ok: s500?.equipment_plan != null,
    });
    items.push({
      label: "Containment(s) erected",
      ok: (s500?.containments.length ?? 0) > 0,
      detail: s500 ? `${s500.containments.length}` : undefined,
    });
    items.push({
      label: "Antimicrobial application logged",
      ok: (s500?.antimicrobials.length ?? 0) > 0,
    });
    items.push({
      label: "Dry standards captured",
      ok: (s500?.dry_standards.length ?? 0) > 0,
      detail: s500 ? `${s500.dry_standards.length}` : undefined,
    });
    items.push({
      label: "Authorization signed",
      ok: (s500?.authorizations.length ?? 0) > 0,
      detail: s500
        ? s500.authorizations.map((a) => a.kind).join(" + ") || undefined
        : undefined,
    });

    const list = document.createElement("div");
    list.style.cssText = "display:flex;flex-direction:column;gap:4px;";
    for (const it of items) list.appendChild(checklistItem(it));
    wrap.appendChild(list);

    const okCount = items.filter((i) => i.ok).length;
    const summary = document.createElement("div");
    summary.style.cssText =
      "font-size:11px;color:var(--text-muted);margin-top:8px;text-align:right;";
    summary.textContent = `${okCount} / ${items.length} complete`;
    wrap.appendChild(summary);

    return wrap;
  }

  private phaseCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const ins of this.opts.manifest?.inspections ?? []) {
      for (const cap of ins.captures) {
        const p: string = (PHASE_ORDER as readonly string[]).includes(cap.phase)
          ? cap.phase
          : "other";
        counts[p] = (counts[p] || 0) + 1;
      }
    }
    return counts;
  }

  private totalCaptures(insps: ManifestInspection[]): number {
    let n = 0;
    for (const i of insps) n += i.captures.length;
    return n;
  }
}

// ── shared bits ─────────────────────────────────────────────────────────────

function section(title: string): HTMLElement {
  const card = document.createElement("section");
  card.style.cssText = `
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  `;
  const h = document.createElement("h3");
  h.textContent = title;
  h.style.cssText =
    "font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.4px;color:var(--text-muted);margin:0;";
  card.appendChild(h);
  return card;
}

function addRow(grid: HTMLElement, label: string, value: string): void {
  const l = document.createElement("div");
  l.style.cssText = "color:var(--text-muted);font-size:12px;";
  l.textContent = label;
  const v = document.createElement("div");
  v.style.cssText = "font-size:13px;color:var(--text);word-break:break-word;";
  v.textContent = value;
  grid.appendChild(l);
  grid.appendChild(v);
}

function legendDot(label: string, count: number, color: string): HTMLElement {
  const w = document.createElement("span");
  w.style.cssText = "display:inline-flex;align-items:center;gap:4px;";
  const dot = document.createElement("span");
  dot.style.cssText = `width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;`;
  const txt = document.createElement("span");
  txt.textContent = `${label} ${count}`;
  w.appendChild(dot);
  w.appendChild(txt);
  return w;
}

function checklistItem(it: { label: string; ok: boolean; detail?: string }): HTMLElement {
  const row = document.createElement("div");
  row.style.cssText =
    "display:flex;align-items:center;gap:8px;font-size:13px;line-height:1.4;";
  const dot = document.createElement("span");
  dot.style.cssText = `
    width: 14px; height: 14px; border-radius: 50%; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    background: ${it.ok ? "rgba(74,255,106,.18)" : "rgba(255,255,255,.05)"};
    color: ${it.ok ? "#4aff6a" : "var(--text-muted)"};
    font-size: 10px; font-weight: 700;
  `;
  dot.textContent = it.ok ? "✓" : "·";
  const label = document.createElement("span");
  label.textContent = it.label;
  label.style.cssText = `color:${it.ok ? "var(--text)" : "var(--text-muted)"};`;
  row.appendChild(dot);
  row.appendChild(label);
  if (it.detail) {
    const detail = document.createElement("span");
    detail.textContent = it.detail;
    detail.style.cssText =
      "font-size:11px;color:var(--text-muted);margin-left:auto;font-variant-numeric:tabular-nums;";
    row.appendChild(detail);
  }
  return row;
}

function formatMoney(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString();
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diffSec = Math.max(0, (Date.now() - t) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

